import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Typography,
  Paper,
  Stack,
  Avatar,
  Divider,
  Chip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { userServices, walletServices } from "../../services/AllServices";
import { fDate } from "../../utils/formatTime";
import { formatPrice } from "../../utils/formatNumber";
import { capitalize } from "lodash";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Diamond, Token } from "@mui/icons-material";

// Validation schema for wallet editing
const walletEditSchema = Yup.object().shape({
  balance: Yup.number()
    .required("Balance is required")
    .typeError("Balance must be a valid number"),
  gemBalance: Yup.number()
    .required("Gem Balance is required")
    .typeError("Gem Balance must be a valid number"),
  sdCoinBalance: Yup.number()
    .required("SD Coin Balance is required")
    .typeError("SD Coin Balance must be a valid number"),
});

function EditWallet() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchUserData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await userServices.getById({ id });
        setData(response?.data?.data?.result);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: walletEditSchema,
    initialValues: {
      balance: 0, 
      gemBalance: 0,
      sdCoinBalance: 0,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          id: id,
          amount: parseFloat(values.balance),
          gemAmount: parseFloat(values.gemBalance),
          sdCoinAmount: parseFloat(values.sdCoinBalance),
        };

        const response = await walletServices.updateWallet(payload);
        toast.success(response?.data?.message || "Wallet updated successfully", { toastId: "success" });
        
        // Navigate back to wallet management with preserved page and view mode
        const page = searchParams.get('page');
        const view = searchParams.get('view');
        
        if (page && view) {
          navigate(`/wallet-management?page=${page}&view=${view}`);
        } else if (page) {
          navigate(`/wallet-management?page=${page}`);
        } else if (view) {
          navigate(`/wallet-management?view=${view}`);
        } else {
          navigate(-1);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit } = formik;

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading user data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Helmet>
        <title>Edit Wallet</title>
      </Helmet>
      <CardLayout>
        <CardHeader title="Edit Wallet" />
        <CardContent>
          {/* User Information Display */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
             <Box>
  <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
    {capitalize(data?.firstname + " " + data?.lastname) || 'N/A'}
  </Typography>

  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
    @{data?.username || 'N/A'}
  </Typography>

  <Box sx={{ display: "flex", gap: 1 }}>
    <Chip
      label={`£${formatPrice(data?.balance || 0)}`}
      sx={{
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        color: '#4caf50',
        fontWeight: 600,
        border: '1px solid',
        borderColor: 'rgba(76, 175, 80, 0.3)'
      }}
    />

    <Chip
      icon={<Diamond color="blue" />}
      label={`${formatPrice(data?.gemBalance || 0)}`}
      sx={{
        backgroundColor: 'rgba(76, 97, 175, 0.2)',
        color: '#4c5baf',
        fontWeight: 600,
        border: '1px solid',
        borderColor: 'rgba(81, 76, 175, 0.3)'
      }}
    />

    <Chip
      icon={<Token style={{ fontSize: '16px', color: '#ffc107' }} />}
      label={`${formatPrice(data?.sdCoinBalance || 0)}`}
      sx={{
        backgroundColor: "rgba(255, 193, 7, 0.2)",
        color: "#ffc107",
        fontWeight: 600,
        border: "1px solid",
        borderColor: "rgba(255, 193, 7, 0.3)",
        '& .MuiChip-icon': {
          color: '#ffc107'
        }
      }}
    />
  </Box>
</Box>
            </Stack>

            <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <PersonIcon sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Full Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                      {capitalize(data?.firstname + " " + data?.lastname) || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <EmailIcon sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                      {data?.email || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <PhoneIcon sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                      {data?.phoneNumber || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Created At
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                      {fDate(data?.createdAt) || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid> */}
            </Grid>
          </Paper>

          {/* Wallet Update Form */}
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                    Manual Balance Adjustment
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Enter an amount to adjust the user's wallet. Use positive numbers to credit and negative numbers to debit (e.g., 500 or -100).
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Adjustment Amount"
                    required
                    name="balance"
                    type="number"
                    value={values.balance}
                    onChange={handleChange}
                    error={Boolean(touched.balance && errors.balance)}
                    helperText={
                      touched.balance && errors.balance
                        ? errors.balance
                        : "Positive to credit, negative to debit (e.g., 500 or -100)"
                    }
                    inputProps={{ 
                      step: 0.01,
                      maxLength: 15 
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                      },
                    }}
                  />
                </Grid>
                 <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Adjustment Gem Amount"
                    required
                    name="gemBalance"
                    type="number"
                    value={values.gemBalance}
                    onChange={handleChange}
                    error={Boolean(touched.gemBalance && errors.gemBalance)}
                    helperText={
                      touched.gemBalance && errors.gemBalance
                        ? errors.gemBalance
                        : "Positive to credit, negative to debit (e.g., 500 or -100)"
                    }
                    inputProps={{ 
                      step: 0.01,
                      maxLength: 15 
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Adjustment SD Coin Amount"
                    required
                    name="sdCoinBalance"
                    type="number"
                    value={values.sdCoinBalance}
                    onChange={handleChange}
                    error={Boolean(touched.sdCoinBalance && errors.sdCoinBalance)}
                    helperText={
                      touched.sdCoinBalance && errors.sdCoinBalance
                        ? errors.sdCoinBalance
                        : "Positive to credit, negative to debit (e.g., 500 or -100)"
                    }
                    inputProps={{ 
                      step: 0.01,
                      maxLength: 15 
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mt={3}>
                    <Stack direction="row" spacing={2}>
                      <LoadingButton
                        size="large"
                        type="submit"
                        variant="contained"
                        loading={loading}
                        sx={{
                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                          }
                        }}
                      >
                        Apply Adjustment
                      </LoadingButton>
                      <LoadingButton
                        size="large"
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        Cancel
                      </LoadingButton>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default EditWallet;